import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Star, ArrowUpRight } from "lucide-react";
import { business, services, areasServed } from "../../mock/mock";

const Footer = () => {
  return (
    <footer className="bg-[#0e0e10] text-white pt-16 pb-8 mt-4">
      <div className="tnf-container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex flex-col leading-none mb-4">
              <span className="font-display text-2xl font-extrabold">
                Talk <span style={{ color: "var(--tnf-blue)" }}>N'</span> Fix
              </span>
              <span className="text-[11px] italic text-white/60 -mt-0.5">
                Freehold
              </span>
            </div>
            <p className="text-white/60 text-[14px] leading-relaxed max-w-xs">
              Freehold's trusted device repair experts. Cell phones, tablets,
              computers & consoles — fixed fast, inside the Freehold Raceway Mall.
            </p>
            <div className="inline-flex items-center gap-2 mt-5 px-3.5 py-2 rounded-full bg-white/5 border border-white/10">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#FFB400" color="#FFB400" />
                ))}
              </div>
              <span className="text-[13px] text-white/80 font-medium">
                {business.rating} · {business.reviewsCount} reviews
              </span>
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-[13px] uppercase tracking-wider text-white/40 font-semibold mb-4">
              Services
            </h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-[14px] text-white/70 hover:text-[var(--tnf-orange)] transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/pre-owned-devices"
                  className="text-[14px] text-white/70 hover:text-[var(--tnf-orange)] transition-colors"
                >
                  Pre-Owned Devices
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <h4 className="text-[13px] uppercase tracking-wider text-white/40 font-semibold mb-4">
              Company
            </h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-[14px] text-white/70 hover:text-[var(--tnf-orange)] transition-colors">Home</Link></li>
              <li><Link to="/services" className="text-[14px] text-white/70 hover:text-[var(--tnf-orange)] transition-colors">All Services</Link></li>
              <li><Link to="/repair-estimate" className="text-[14px] text-white/70 hover:text-[var(--tnf-orange)] transition-colors">Book a Repair</Link></li>
              <li><Link to="/contact" className="text-[14px] text-white/70 hover:text-[var(--tnf-orange)] transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="text-[13px] uppercase tracking-wider text-white/40 font-semibold mb-4">
              Visit / Call
            </h4>
            <ul className="space-y-3.5">
              <li>
                <a href={business.phoneRaw} className="flex items-start gap-3 text-[14px] text-white/70 hover:text-white transition-colors">
                  <Phone size={16} className="mt-0.5 text-[var(--tnf-orange)]" />
                  {business.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${business.email}`} className="flex items-start gap-3 text-[14px] text-white/70 hover:text-white transition-colors">
                  <Mail size={16} className="mt-0.5 text-[var(--tnf-orange)]" />
                  {business.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-[14px] text-white/70">
                <MapPin size={16} className="mt-0.5 text-[var(--tnf-orange)] flex-shrink-0" />
                <span>
                  {business.addressLine1}, {business.addressLine2}
                  <span className="block text-white/50 mt-0.5">{business.floor}</span>
                </span>
              </li>
              <li className="flex items-start gap-3 text-[14px] text-white/70">
                <Clock size={16} className="mt-0.5 text-[var(--tnf-orange)] flex-shrink-0" />
                <span>
                  {business.hours.map((h) => (
                    <span key={h.day} className="block">
                      {h.day}: {h.time}
                    </span>
                  ))}
                </span>
              </li>
            </ul>
            <a
              href={business.directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tnf-pill mt-5 text-[13px] pl-4 pr-1.5 py-1.5"
              style={{ background: "var(--tnf-orange)", color: "#fff" }}
            >
              Get Directions
              <span className="tnf-arrow w-7 h-7 bg-white">
                <ArrowUpRight size={15} color="var(--tnf-orange)" />
              </span>
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6">
          <p className="text-[13px] text-white/45 text-center mb-5">
            Proudly serving{" "}
            <span className="text-white/70">{areasServed.join(", ")}</span> and
            all of Monmouth County, NJ.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[13px] text-white/40">
              © {new Date().getFullYear()} Talk N' Fix · Freehold Raceway Mall, NJ. All rights reserved.
            </p>
            <p className="text-[13px] text-white/40">
              Website by{" "}
              <a
                href="https://pharosgrowth.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-white/70 hover:text-[var(--tnf-blue)] transition-colors"
              >
                Pharos Growth
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
